import { Components } from '.generated/templates/Feature.Components.model';
import { Sitecore } from '.generated/templates/_.Sitecore.Override';
import Button from 'components/helpers/Button';
import Container from 'components/helpers/Container';
import { MissingDataSource } from 'components/helpers/EditingHelpText';
import Heading, { propsFromFields } from 'components/helpers/Heading';
import { classnames } from 'tailwindcss-classnames';
import { ButtonVariant } from 'components/helpers/Button/WithButton';
import ImageWrapper from 'components/helpers/ImageWrapper/ImageWrapper';
import ChecklistCircle from 'components/helpers/SvgIcon/icons/checklist-circle';
import RichTextA11yWrapper from 'components/helpers/RichTextA11yWrapper';
import useExperienceEditor from 'lib/sitecore/use-experience-editor';

export type ChecklistBlockProps = Sitecore.Override.ComponentBase &
  Components.General.ChecklistBlock.Fields.ChecklistBlock;

const ChecklistBlock = ({ fields }: ChecklistBlockProps): JSX.Element => {
  const isExperienceEditor = useExperienceEditor();
  if (!fields) {
    return <MissingDataSource />;
  }
  const headingProps = propsFromFields(fields);
  const cta2TypeField = fields.cta2Type.value;
  const cta2Type: ButtonVariant =
    cta2TypeField === 'Default' || cta2TypeField.trim().length === 0
      ? 'link'
      : (cta2TypeField.toLowerCase() as ButtonVariant);
  const nextImageField = {
    ...fields.Image,
    value: {
      ...fields.Image.value,
      width: '300',
      height: '400',
    },
  };
  let checklistTexture = 'checklist-texture';
  checklistTexture += ` ${classnames('h-40', 'md:h-40', 'xl:ml-24', 'lg:mx-16', 'mx-8')}`;

  return (
    <Container
      size="standard"
      className={classnames(
        'flex',
        'theme-navy-blue',
        'text-white',
        'bg-theme-bg',
        'overflow-hidden',
        'max-w-screen-2xl',
        'mx-auto',
        'w-full',
        'relative',
        'left-0',
        'right-0',
        'px-4'
      )}
      dataComponent="authorable/general/checklist-block"
    >
      <div className={classnames('w-full', 'md:flex')}>
        <div className={classnames('w-full', 'z-10', 'md:w-5/12', 'h-auto', 'flex', 'flex-col')}>
          <div className={checklistTexture} />
          <div className={classnames('h-auto', 'xl:ml-24', 'lg:mx-16', 'mx-8')}>
            <ImageWrapper
              field={nextImageField}
              layout="responsive"
              className={classnames('rounded-t-full')}
            />
          </div>
        </div>
        <div
          className={classnames(
            'w-full',
            'flex',
            'flex-col',
            'md:w-7/12',
            'relative',
            'justify-center',
            'my-3',
            'md:ml-6'
          )}
        >
          <div>
            <Heading {...headingProps} className={classnames('mb-4')} />
            <div
              className={classnames(
                'flex',
                'flex-col',
                'md:flex-row',
                'items-baseline',
                'md:flex-wrap'
              )}
            >
              <Button
                linkField={fields.cta1}
                className={classnames(
                  'mt-6',
                  'mr-2',
                  'sm:mr-6',
                  'w-full',
                  'md:w-auto',
                  'py-3',
                  'text-center'
                )}
              />
              <Button
                linkField={fields.cta2}
                variant={cta2Type}
                className={classnames('mt-4', 'w-full', 'md:w-auto', 'py-3', 'text-center')}
              />
            </div>
            {isExperienceEditor ? (
              <div className={classnames('flex', 'flex-wrap', 'mt-10')}>
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox1}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox2}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox3}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox4}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox5}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox6}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox7}
                  editable
                />
                <RichTextA11yWrapper
                  className={classnames('flex', 'basis-1/2')}
                  field={fields.Checkbox8}
                  editable
                />
              </div>
            ) : (
              <div className={classnames('md:flex', 'md:flex-wrap', 'mt-10')}>
                {fields.Checkbox1.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox1}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox2.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox2}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox3.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox3}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox4.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox4}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox5.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox5}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox6.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox6}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox7.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox7}
                      editable
                    />
                  </span>
                )}
                {fields.Checkbox8.value !== '' && (
                  <span className={classnames('flex', 'md:basis-1/2', 'my-3')}>
                    <ChecklistCircle />
                    <RichTextA11yWrapper
                      className={classnames('ml-3')}
                      field={fields.Checkbox8}
                      editable
                    />
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Container>
  );
};

export default ChecklistBlock;
